import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetchSkpEnvironment, fetchTransfer } from "../../api";
import {
  ApiException,
  PasswordProtectedTransfer,
  SkpEnvironment,
  Transfer,
  TransferProtection,
} from "../../entities";
import {
  ConditionalEnvironmentProvider,
  EnvironmentProvider,
} from "../../providers/environment";
import TransferPageSkeleton from "../skeletons/transfer";
import BlockedPage from "./locked/blocked";
import PasswordProtectedPage from "./locked/passwordProtected";
import { RecipientAuthenticatedPage } from "./locked/recipientAuthenticated";
import NotFoundPage from "./notFound";
import SuccessPage from "./success";
import LoadingScreen from "../loading";
import { useWindowDimensions } from "../../hooks/useWindowDimensions";
import {
  getErrorMessage,
  isApiException,
  isPasswordProtectedTransfer,
  isTransfer,
} from "../../utils/typeGuard";
import { ApiExceptionErrorPage, ErrorPage } from "../error";
import { getLinkHeaderUri } from "../../utils/url";

type SuccessContentProp = {
  transfer: Transfer | PasswordProtectedTransfer;
};

export default function TransferPage() {
  const { transferUid } = useParams();

  // the router should not allow this to happen
  if (!transferUid) throw new Error("transfer uid is undefined");

  // This stores / refers to environment data, if fetch successfully,
  // so that NOT-FOUND & BLOCKED transfer GET pages can
  // use environment's values, as per their need, even if fetching transfer fails.
  const environmentRef = useRef<SkpEnvironment | undefined>(undefined);

  const { t } = useTranslation();
  const { status, data, error } = useQuery({
    retry: false,
    queryKey: ["transferAndEnvironment", transferUid],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      let environment: SkpEnvironment = await fetchSkpEnvironment();

      environmentRef.current = environment;

      let transfer: Transfer | PasswordProtectedTransfer = await fetchTransfer(
        transferUid
      );

      return { environment, transfer };
    },
  });
  const { isMobileWidth } = useWindowDimensions();
  const childrenVerticalAlignment: string = isMobileWidth ? "start" : "center";

  switch (status) {
    case "loading":
      return <LoadingScreen />;
    case "error":
      if (error instanceof AxiosError && error.response) {
        if (error.response.status === 404) {
          return (
            <ConditionalEnvironmentProvider
              condition={!!environmentRef.current}
              environment={environmentRef.current}
            >
              <TransferPageSkeleton
                childrenVerticalAlignment="center"
                hasMailboxLoaded={false}
              >
                <NotFoundPage />
              </TransferPageSkeleton>
            </ConditionalEnvironmentProvider>
          );
        }

        if (isApiException(error.response.data)) {
          const apiException: ApiException = error.response
            .data as ApiException;
          const rawErrorMessage: string = getErrorMessage(apiException, t);

          // Error reponse code: 403068 means that the transfer has protection
          // of type recipient authenticated and either the user is not the sender or recipient
          // or user tried to access this transfer in a browser which doesnt has `signed-in` cookie stored.
          // https://dev.skalio.net/teambeam/api/v2/#accessing-transfers-transfers-get-3

          if (apiException.error.code === 403068) {
            // skp-server tells us, on which hostname the recipient has to log in
            // (or self-register) to access this transfer, via the `Link` response
            // header with rel="alternate". Since skp-light is served off the
            // sender's hostname, this may differ from the current hostname.
            // https://gitlab.skalio.net/skp/skp-light/-/work_items/230
            const alternateTransferUrl: string | undefined = getLinkHeaderUri(
              error.response.headers?.["link"],
              "alternate"
            );

            return (
              <ConditionalEnvironmentProvider
                condition={!!environmentRef.current}
                environment={environmentRef.current}
              >
                <div className={`animate-fade-in`}>
                  <TransferPageSkeleton
                    childrenVerticalAlignment="center"
                    hasMailboxLoaded={false}
                  >
                    <RecipientAuthenticatedPage
                      transferFetchForbidden403068
                      transferUid={transferUid}
                      rawApiErrorMessage={rawErrorMessage}
                      alternateTransferUrl={alternateTransferUrl}
                    />
                  </TransferPageSkeleton>
                </div>
              </ConditionalEnvironmentProvider>
            );
          }

          return (
            <ConditionalEnvironmentProvider
              condition={!!environmentRef.current}
              environment={environmentRef.current}
            >
              <TransferPageSkeleton
                childrenVerticalAlignment="center"
                hasMailboxLoaded={false}
              >
                <ApiExceptionErrorPage errorMessage={rawErrorMessage} />
              </TransferPageSkeleton>
            </ConditionalEnvironmentProvider>
          );
        }
      }

      return <ErrorPage errorMessage={(error as Error).message} />;
    case "success":
      return (
        <EnvironmentProvider environment={data.environment}>
          <div className={`animate-fade-in`}>
            <TransferPageSkeleton
              childrenVerticalAlignment={childrenVerticalAlignment}
              hasMailboxLoaded={true}
            >
              <SuccessContent transfer={data.transfer} />
            </TransferPageSkeleton>
          </div>
        </EnvironmentProvider>
      );
  }
}

function SuccessContent(successContentProp: SuccessContentProp) {
  const { t } = useTranslation();
  const [unlockedOrBlockedTransfer, setUnlockedOrBlockedTransfer] = useState<
    Transfer | PasswordProtectedTransfer | null
  >(null);

  const transfer = unlockedOrBlockedTransfer ?? successContentProp.transfer;

  if (transfer.blocked) {
    const cause: string = t(
      `The transfer you requested is no longer available because the required password was entered incorrectly for the third time.`
    );
    return <BlockedPage cause={cause} />;
  } else if (
    transfer.protection === TransferProtection.password &&
    transfer.locked
  ) {
    if (!isPasswordProtectedTransfer(transfer)) {
      return (
        <ErrorPage errorMessage={t(`Invalid password protected transfer.`)} />
      );
    }

    return (
      <PasswordProtectedPage
        transferUid={transfer.uid}
        passwordAttemptsLeft={transfer.passwordAttempts}
        onTransferUnlockOrBLocked={(unlockedOrBlockedTransfer) => {
          setUnlockedOrBlockedTransfer(unlockedOrBlockedTransfer);
        }}
      />
    );
  } else if (
    transfer.protection === TransferProtection.recipientAuthentication
  ) {
    if (!isTransfer(transfer)) {
      return (
        <ErrorPage
          errorMessage={t(`Invalid recipient authenticated transfer.`)}
        />
      );
    }

    return (
      <RecipientAuthenticatedPage
        recipientId={transfer.uid}
        recipientEmail={transfer.recipientAddress}
        senderEmail={transfer.senderAddress}
      />
    );
  } else {
    if (!isTransfer(transfer)) {
      return <ErrorPage errorMessage={t(`Invalid transfer.`)} />;
    }

    return <SuccessPage transfer={transfer} />;
  }
}
